/**
 * URL sanitization utility.
 * Rejects `javascript:`, `data:`, `vbscript:`, `file:` and other non-http(s)
 * schemes to prevent stored XSS via profile links, meeting URLs, or any
 * user-supplied external link.
 */

const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:'];

export function sanitizeExternalUrl(input: string): string {
  if (typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';

  // Quick reject: starts with dangerous protocol or path traversal
  const lower = trimmed.toLowerCase();
  const dangerousPrefixes = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];
  for (const prefix of dangerousPrefixes) {
    if (lower.startsWith(prefix)) return '';
  }

  // Parse URL
  try {
    const url = new URL(trimmed);
    if (!ALLOWED_PROTOCOLS.includes(url.protocol)) return '';
    // Reject if hostname is just an IP that resolves to private network
    // (optional, light SSRF prevention for image proxies)
    return url.toString();
  } catch {
    // Not a full URL — try adding https://
    if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(trimmed) && !trimmed.includes(' ')) {
      try {
        const url = new URL('https://' + trimmed);
        if (!ALLOWED_PROTOCOLS.includes(url.protocol)) return '';
        return url.toString();
      } catch {
        return '';
      }
    }
    return '';
  }
}

export function isValidHttpUrl(string: string): boolean {
  if (typeof string !== 'string') return false;
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}
